import React, { memo } from 'react'
import { Provider, createStore } from 'jotai'
import '../ui-kit/index.css'

import {
  appState,
  layoutState,
  leftSidebarState,
  leftSidebarToggleShowState,
  leftSidebarVersionShowState,
} from '../state'
import { FormatProvider } from '../utils/format'
import NavMenu, { NavMenuItem } from '../Layout/NavMenu'
import { Notification } from '@caudena/web-platform'
import { NavMenuSlot } from '../Layout/slots'
import { Atom } from 'jotai'

interface AppSlots {
  sidebar?: React.ReactNode
  sidebarTop?: React.ReactNode
  sidebarBottom?: React.ReactNode
  navbar?: React.ReactNode
  search?: React.ReactNode
  userDropdown?: React.ReactNode
}

interface RouterParams {
  [key: string]: string
}

type ParamsState = {
  coin: string
}

interface AppProps {
  navItems: NavMenuItem[]
  initialState?: Record<string, unknown>
  defaultParams?: RouterParams
  slots?: AppSlots
  paramsState?: Atom<ParamsState>
  sidebarTop?: React.ComponentType<{ isOpen: boolean }>
  sidebarBottom?: React.ComponentType<{ isOpen: boolean }>
  leftSidebarState?: boolean
  leftSidebarToggleShow?: boolean
  leftSidebarVersionShow?: boolean
  logout: () => void
  children: React.ReactNode
}

function JotaiProvider({
  layoutState: layoutInitialState,
  appState: appInitialState = {},
  leftSidebarState: leftSidebarInitialState = true,
  leftSidebarVersionShow: leftSidebarVersionShowInitialState = true,
  leftSidebarToggleShow: leftSidebarToggleShowInitialState = true,
  children,
}) {
  const store = React.useMemo(() => {
    const s = createStore()
    s.set(layoutState, layoutInitialState)
    s.set(appState, appInitialState)
    s.set(leftSidebarState, leftSidebarInitialState)
    s.set(leftSidebarToggleShowState, leftSidebarToggleShowInitialState)
    s.set(leftSidebarVersionShowState, leftSidebarVersionShowInitialState)
    return s
  }, [])

  return <Provider store={store}>{children}</Provider>
}

const AppContent = ({
  navItems,
  paramsState,
  initialState,
  defaultParams,
  slots,
  sidebarTop,
  sidebarBottom,
  logout,
  leftSidebarState,
  leftSidebarVersionShow,
  leftSidebarToggleShow,
  children,
}: AppProps) => {
  const layoutOptions = {
    navItems,
    defaultParams,
    sidebar: slots?.sidebar,
    sidebarTop: slots?.sidebarTop,
    sidebarBottom: slots?.sidebarBottom,
    navbar: slots?.navbar,
    search: slots?.search,
    userDropdown: slots?.userDropdown,
  }

  NavMenuSlot.useContent(
    ({ isOpen }) => (
      <NavMenu
        topComponent={sidebarTop}
        bottomComponent={sidebarBottom}
        logout={logout}
        navItems={navItems}
        paramsState={paramsState}
        defaultParams={defaultParams}
        isOpen={isOpen}
      />
    ),
    [navItems, logout]
  )

  return (
    <JotaiProvider
      leftSidebarVersionShow={leftSidebarVersionShow}
      leftSidebarState={leftSidebarState}
      layoutState={layoutOptions}
      leftSidebarToggleShow={leftSidebarToggleShow}
      appState={initialState}
    >
      <FormatProvider>
        {children}
        <Notification.Container />
      </FormatProvider>
    </JotaiProvider>
  )
}

export default memo(AppContent)
